import { useId, useState } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import {
  DESIGN_SIZE_CATEGORIES,
  PROGRAM_LABELS,
  formatSizeLabel,
  presetToPixels,
  type DesignSizePreset,
} from '../designSizes';

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  store: RaeditorStore;
};

type PageLike = {
  id: string;
  set?: (attrs: Record<string, unknown>) => void;
};

function applyProgramLabels(store: RaeditorStore) {
  const pages = (store.pages || []) as PageLike[];
  pages.forEach((page, i) => {
    const label = PROGRAM_LABELS[i] || `Page ${i + 1}`;
    page.set?.({
      custom: { label, programPanel: i + 1, name: label },
    });
  });
}

export function applyDesignPreset(store: RaeditorStore, preset: DesignSizePreset): string {
  const { width, height, dpi, unit } = presetToPixels(preset);
  const pageCount = preset.pages ?? 1;

  store.clear();
  if (typeof store.setUnit === 'function') {
    store.setUnit({ unit, dpi });
  }
  store.setSize?.(width, height, false);

  if (pageCount <= 1) {
    const page = store.addPage({ width, height }) as PageLike;
    page.set?.({ width, height });
  } else {
    for (let i = 0; i < pageCount; i++) {
      const label = PROGRAM_LABELS[i] || `Page ${i + 1}`;
      const page = store.addPage({
        width,
        height,
        custom: { label, programPanel: i + 1, name: label },
      }) as PageLike;
      page.set?.({
        width,
        height,
        custom: { label, programPanel: i + 1, name: label },
      });
    }
    applyProgramLabels(store);
  }

  store.history.clear();
  const pages = (store.pages || []) as PageLike[];
  if (pages[0]?.id) store.selectPage?.(pages[0].id);

  const sizeTxt = formatSizeLabel(width, height);
  if (pageCount > 1) {
    return `New ${pageCount}-page design · ${preset.label} · ${sizeTxt}`;
  }
  return `New design · ${preset.label} · ${sizeTxt}`;
}

export function NewDesignModal({ open, onClose, onStatus, store }: Props) {
  const titleId = useId();
  const [selectedId, setSelectedId] = useState('ig-post');

  if (!open) return null;

  const flat = DESIGN_SIZE_CATEGORIES.flatMap((c) =>
    c.presets.map((p) => ({ ...p, category: c.name })),
  );
  const selected = flat.find((p) => p.id === selectedId) || flat[0];

  const handleCreate = () => {
    if (!selected) return;
    const pages = (store.pages || []) as Array<{ children?: unknown[] }>;
    const hasContent = pages.some((p) => Array.isArray(p.children) && p.children.length > 0);
    if (hasContent || pages.length > 1) {
      if (
        !window.confirm(
          `Replace the current design with “${selected.label}”? Unsaved changes will be lost.`,
        )
      ) {
        return;
      }
    }
    const msg = applyDesignPreset(store, selected);
    onStatus(msg);
    onClose();
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--wide modal--sizes"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            New design
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Choose a layout size. Social presets are in pixels; print uses inches @ 300 dpi where noted.
          Unsaved work on the canvas will be replaced.
        </p>

        <div className="size-picker">
          {DESIGN_SIZE_CATEGORIES.map((cat) => (
            <section key={cat.id} className="size-picker__cat">
              <h3 className="size-picker__cat-title">{cat.name}</h3>
              <div className="size-picker__grid">
                {cat.presets.map((preset) => {
                  const px = presetToPixels(preset);
                  const active = selectedId === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`size-picker__card${active ? ' size-picker__card--active' : ''}`}
                      onClick={() => setSelectedId(preset.id)}
                      aria-pressed={active}
                    >
                      <span className="size-picker__card-label">{preset.label}</span>
                      <span className="size-picker__card-dims">
                        {preset.unit === 'px'
                          ? `${preset.width}×${preset.height}`
                          : `${preset.width}×${preset.height} ${preset.unit}`}
                        {preset.pages && preset.pages > 1 ? ` · ${preset.pages} pages` : ''}
                      </span>
                      <span className="size-picker__card-px">{formatSizeLabel(px.width, px.height)}</span>
                      {preset.hint && <span className="size-picker__card-hint">{preset.hint}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--accent" onClick={handleCreate}>
            Create {selected ? `· ${selected.label}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
